import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleModal } from '@/components/SimpleModal';
import { Card, CardContent } from '@/components/ui/card';
import { formatCurrency } from '@/lib/utils';
import {
  Wallet,
  Clock,
  CircleDollarSign,
  EyeOff,
  CheckCircle2,
  XCircle,
  RefreshCw
} from 'lucide-react';

const API_URL = BACKEND_URL;

export const CaixaModal = ({
  isOpen,
  onClose,
  tipo, // 'abertura' ou 'fechamento'
  caixaAtual,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Abertura
  const [valorInicial, setValorInicial] = useState('');
  const [observacaoAbertura, setObservacaoAbertura] = useState('');

  // Fechamento (às cegas: o operador lança o que apurou, sem ver o esperado)
  const [formas, setFormas] = useState([]);
  const [valores, setValores] = useState({});
  const [resumo, setResumo] = useState(null);
  const [observacaoFechamento, setObservacaoFechamento] = useState('');

  const carregarFormas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caixa/atual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      const conferencia = (data.formas || []).filter((f) => f.tipo === 'conferencia');
      setFormas(conferencia);
      setResumo(data.resumo || null);
      setValores(Object.fromEntries(conferencia.map((f) => [f.code, ''])));
    } catch (err) {
      setError('Não foi possível carregar as formas de pagamento do caixa');
    }
  }, []);

  useEffect(() => {
    if (isOpen && tipo === 'fechamento') carregarFormas();
  }, [isOpen, tipo, carregarFormas]);

  const resetForm = () => {
    setValorInicial('');
    setObservacaoAbertura('');
    setValores({});
    setFormas([]);
    setResumo(null);
    setObservacaoFechamento('');
    setError(null);
  };

  const handleAbertura = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/caixa/abrir`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          valor_inicial: parseFloat(valorInicial) || 0,
          observacao: observacaoAbertura || null
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onSuccess(data.caixa);
        onClose();
        resetForm();
      } else {
        setError(data.detail || 'Erro ao abrir caixa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFechamento = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        valores: Object.fromEntries(formas.map((f) => [f.code, parseFloat(valores[f.code]) || 0])),
        observacao: observacaoFechamento || null
      };
      const response = await fetch(`${API_URL}/api/caixa/fechar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onSuccess(data.resumo);
        onClose();
        resetForm();
      } else {
        setError(data.detail || 'Erro ao fechar caixa');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalInformado = formas.reduce((sum, f) => sum + (parseFloat(valores[f.code]) || 0), 0);

  if (tipo === 'abertura') {
    return (
      <SimpleModal open={isOpen} onClose={onClose} title="Abrir Caixa">
        <div className="space-y-4" data-testid="caixa-abertura-modal">
          <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg">
            <Wallet className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="font-medium text-emerald-900">Abertura de Caixa</p>
              <p className="text-sm text-emerald-700">Informe o valor inicial em dinheiro</p>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div>
            <Label htmlFor="valorInicial">Valor Inicial (Dinheiro)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">R$</span>
              <Input
                id="valorInicial"
                type="number"
                step="0.01"
                min="0"
                value={valorInicial}
                onChange={(e) => setValorInicial(e.target.value)}
                placeholder="0,00"
                className="pl-10"
                autoFocus
                data-testid="input-valor-inicial"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="observacao">Observação (opcional)</Label>
            <Input
              id="observacao"
              value={observacaoAbertura}
              onChange={(e) => setObservacaoAbertura(e.target.value)}
              placeholder="Ex: Troco conferido com gerente"
              data-testid="input-observacao-abertura"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleAbertura}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-abrir-caixa"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Abrir Caixa
            </Button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  // Fechamento às cegas
  return (
    <SimpleModal open={isOpen} onClose={onClose} title="Fechar Caixa" className="max-w-2xl">
      <div className="space-y-4" data-testid="caixa-fechamento-modal">
        <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
          <EyeOff className="w-8 h-8 text-amber-600 shrink-0" />
          <div>
            <p className="font-medium text-amber-900">Fechamento às cegas</p>
            <p className="text-sm text-amber-700">
              Conte a gaveta e as maquininhas e lance o valor apurado em cada forma de pagamento. A
              conferência com o sistema fica disponível para o gerente depois do fechamento.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3" data-testid="fechamento-resumo">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Abertura</p>
              <p className="font-bold text-lg">
                {formatCurrency(resumo?.valor_inicial ?? caixaAtual?.valor_inicial ?? 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Vendas</p>
              <p className="font-bold text-lg text-emerald-600">
                {resumo?.vendas_count ?? caixaAtual?.vendas_count ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Sangrias / Supr.</p>
              <p className="font-bold text-sm">
                <span className="text-red-600">-{formatCurrency(resumo?.total_sangrias || 0)}</span>
                {' / '}
                <span className="text-blue-600">+{formatCurrency(resumo?.total_suprimentos || 0)}</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Valores apurados por forma de pagamento (parametrizadas pelo tenant) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {formas.map((forma) => (
            <div key={forma.code}>
              <Label className="flex items-center gap-2">
                <CircleDollarSign className="w-4 h-4 text-slate-500" />
                {forma.label}
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={valores[forma.code] ?? ''}
                onChange={(e) => setValores({ ...valores, [forma.code]: e.target.value })}
                placeholder="0,00"
                data-testid={`input-valor-${forma.code}`}
              />
            </div>
          ))}
          {formas.length === 0 && (
            <p className="text-sm text-slate-500 col-span-2">Carregando formas de pagamento...</p>
          )}
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <span className="text-sm text-slate-600">Total apurado (informado):</span>
          <span className="font-bold text-lg" data-testid="total-informado">
            {formatCurrency(totalInformado)}
          </span>
        </div>

        <div>
          <Label htmlFor="observacaoFechamento">Observação (opcional)</Label>
          <Input
            id="observacaoFechamento"
            value={observacaoFechamento}
            onChange={(e) => setObservacaoFechamento(e.target.value)}
            placeholder="Ex: Conferido com gerente, sangria realizada"
            data-testid="input-observacao-fechamento"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleFechamento}
            disabled={loading || formas.length === 0}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="btn-fechar-caixa"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
            Fechar Caixa
          </Button>
        </div>
      </div>
    </SimpleModal>
  );
};
